/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Observable, Subject } from 'rxjs';

export function observeProperty<T extends object, K extends keyof T>(obj: T, prop: K): Observable<T[K]> {
  const propertyObserver = getPropertyObserver(obj);

  if (!propertyObserver[prop]) {
    const subject = new Subject<T[K]>();
    onChange(obj, prop, newValue => subject.next(newValue));
    propertyObserver[prop] = subject.asObservable();
  }

  return propertyObserver[prop];
}

/**
 * creates or returns existing property observer for object
 */
function getPropertyObserver<T extends object>(obj: T): { [K in keyof T]?: Observable<T[K]> } {
  const PROPERTY_OBSERVER_KEY = 'applause_observe_property';

  if (!obj[PROPERTY_OBSERVER_KEY]) {
    Object.defineProperty(obj, PROPERTY_OBSERVER_KEY, {enumerable: false, value: {}});
  }

  return obj[PROPERTY_OBSERVER_KEY];
}

function onChange<T extends object, K extends keyof T>(obj: T, prop: K, callback: (value: T[K]) => void) {
  const descriptor = createDescriptor(obj, prop);
  const setter = descriptor.set;

  /**
   * Override descriptor setter, calling original and emitting callback.
   */
  descriptor.set = function(value: T[K]) {
    if (setter) {
      setter.call(this, value);
    }
    callback(value);
  };

  descriptor.configurable = true;
  Object.defineProperty(obj, prop, descriptor);
}

/**
 * Create PropertyDescriptor, checking prototype chain if needed (for NG18 signal inputs).
 */
function createDescriptor<T extends object, K extends keyof T>(obj: T, prop: K): PropertyDescriptor {
  let descriptor = Object.getOwnPropertyDescriptor(obj, prop);

  // Check prototype chain if not on instance
  if (!descriptor) {
    let proto = Object.getPrototypeOf(obj);
    while (proto && proto !== Object.prototype) {
      const protoDescriptor = Object.getOwnPropertyDescriptor(proto, prop);
      if (protoDescriptor) {
        descriptor = protoDescriptor;
        break;
      }
      proto = Object.getPrototypeOf(proto);
    }
  }

  // Fallback: create default descriptor
  if (!descriptor) {
    const currentValue = obj[prop];
    descriptor = {
      value: currentValue,
      writable: true,
      enumerable: true,
      configurable: true
    };
  }

  const result = { ...descriptor };

  /**
   * If descriptor has value and value is writable, then replace it with getter and setter, so we can track changes
   */
  if (result.hasOwnProperty('value') && result.writable) {
    let value = result.value;

    result.set = v => value = v;
    result.get = () => value;

    delete result.value;
    delete result.writable;
  }

  return result;
}
